import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { PartQuote, SFOrderHistory, TwoWayOrderHistoryData } from 'types';
import { saveAs } from 'file-saver';
import {
  useGetOrderMachinesQuery,
  useGetOrdersQuery,
  useGetQuotesQuery,
  useLazyGetProductsQuery,
  useAddProductToRemoteCartMutation,
  useGetUserQuery,
  useDeleteRequestQuoteMutation,
  iopsApi
} from 'api';
import { useDispatch } from 'react-redux';
import { cartActions } from 'actions';
import { convertUTCDateTimeToLocalDateTime } from 'common/helpers/v1';
import { OrdersContainer } from './NewSFOrdersTableStyled';
import { useTranslation } from 'react-i18next';
import { PageHeader, TabNav } from 'components';
import { Product } from 'types/parts';
import { UNIVERSAL_PRODUCTS_KEY } from 'constants/cart';
import { tanStackTableActions as tanActions } from 'actions/tanStackTable';
import { NavContainer } from 'pages/PartsCatalogPage/PartsShop.element';
import { useHistory } from 'react-router-dom';
import Cube from 'icons/indexed-icons/Cube';
import OrderQuote from 'icons/indexed-icons/OrderQuote';
import OrderHistory from './OrderHistor';
import QuoteRequest from './QuoteRequest';
import OrderPdfModal from './OrderPdfModal';
import QuotePdfModal from './QuotePdfModal';
import OrdersIcon from 'icons/img/OrderIconNew.svg';
import TwoWayOrderHistory from './TwoWayOrderHistory';
import TwoWayOrderQuote from './TwoWayOrderQuote';
import { ComposeCartItem } from 'components/MachinePartsTable/CartHelp';
import { CartItem } from 'types/parts/cart';
import { ToastMsg } from 'common/components/Toast';
import {
  ORDERS_TAB,
  QUOTES_TAB,
  ORDER_STATUS_FILTER_OPTIONS,
  QUOTE_STATUS_FILTER_OPTIONS
} from './constants';

export const ITEMS_PER_PAGE = 20;

const NewSFOrdersTable = (): ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common']);
  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const tab = searchParams.get('tab');
  const [currentTab, setCurrentTab] = useState<string>(tab || ORDERS_TAB);
  const [showOrderDetails, setShowOrderDetails] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentQuotePage, setCurrentQuotePage] = useState(0);
  const [machineId, setMachineId] = useState<string>('all_machine');
  const [linkToPdf, setLinkToPdf] = useState<string>();
  const [poNumber, setPONumber] = useState<string>('');
  const [quoteModalItem, setQuoteModalItem] = useState<PartQuote>();
  const [showQuoteDetails, setShowQuoteDetails] = useState<boolean>(false);
  const [OrderSearchKey, setOrderSearchKey] = useState<string>('');
  const [OrderFilter, setOrderFilter] = useState<string>('');
  const [QuoteSearchKey, setQuoteSearchKey] = useState<string>('');
  const [QuoteFilter, setQuoteFilter] = useState<string>('');



  const { data: userData } = useGetUserQuery();

  const isTwoWaySyncUser = useMemo(() => {
    if (userData && userData?.isTwoWaySyncUser) {
      return userData?.isTwoWaySyncUser;
    }
    return false;
  }, [userData]);

  const { data: OrderedOnMachines } = useGetOrderMachinesQuery();
  const { data: orders, isFetching: isDataFetching }  = useGetOrdersQuery({
    machineId: machineId,
    isTwoWayCall: false,
    searchKey: OrderSearchKey,
    filter: OrderFilter,
    limit: ITEMS_PER_PAGE,
    offset: currentPage
  });
  const { data: orderQuotes, isFetching: isQuoteFetching, refetch: refetchOrderQuotes } = useGetQuotesQuery({
    isTwoWayCall: false,
    searchKey: QuoteSearchKey,
    filter: QuoteFilter,
    limit: ITEMS_PER_PAGE,
    offset: currentQuotePage
  });

  const [getProductsDetail] = useLazyGetProductsQuery();
  const [AddProductToRemoteCart] = useAddProductToRemoteCartMutation();
  const [deleteRequestQuote] = useDeleteRequestQuoteMutation();

  const sfOrders = useMemo(() => orders?.orderDetails, [orders]) as SFOrderHistory[];

  const machines = useMemo(() => {
    if (OrderedOnMachines?.machineList && OrderedOnMachines.machineList.length > 0) {
      const mlist = OrderedOnMachines.machineList?.map((m) => {
        return { value: m.machineId, label: m.machineName };
      });
      return mlist;
    }
  }, [OrderedOnMachines]);

  useEffect(() => {
    if (currentTab === ORDERS_TAB) {
      dispatch(tanActions.addFilterOptions(isTwoWaySyncUser ? ORDER_STATUS_FILTER_OPTIONS : (machines || [])));
      return;
    }

    if (currentTab === QUOTES_TAB) {
      dispatch(tanActions.addFilterOptions(QUOTE_STATUS_FILTER_OPTIONS));
    }
  }, [currentTab, dispatch, machines]);
  

  const hideOrderModal = () => {
    setShowOrderDetails(!showOrderDetails);
  };
  const hideQuoteModal = () => {
    setShowQuoteDetails(!showQuoteDetails);
  };
  const showOrderModal = (order: SFOrderHistory | TwoWayOrderHistoryData) => {
    if (order !== undefined) {
      setLinkToPdf(order.linkToPdfInBlobStorage as string);
      setPONumber((order.poNumber as string) || '');
      setShowOrderDetails(!showOrderDetails);
    }
  }; // list pening items to be moved to next sprint
  const showQuoteModal = (quote: PartQuote) => {
    if (quote !== undefined) {
      setShowQuoteDetails(!showQuoteDetails);
      setQuoteModalItem(quote);
    }
  };
  const formatPODate = (date: string | undefined): string => {
    if (!date) return '';
    if (date === 'N/A') return 'N/A';
    return convertUTCDateTimeToLocalDateTime(date);
  };

  const addTwoWayItemsToCart = async (order: TwoWayOrderHistoryData) => {
    if (sfOrders !== undefined) {
      // change sfOrders to point twoway order object
      order.products?.forEach((item) => {
        const product: Product = {
          id: item.productId as string,
          description: item.description as string,
          sku: item.partNumber as string,
          price: Number(item.unitPrice),
          priceUnit: item.currency as string,
          isPurchasable: true,
          leadTime: item?.leadTime || 0,
          stock: item?.stock || 1,
          businessUnit: 'proseal'
        };
        AddProductToRemoteCart([ComposeCartItem(product, item.machineId as string, item.machineName as string)] as CartItem[])
              .unwrap()
              .then(() => {
                                // maintain temporay FE cartState to track cart count
                      dispatch({
                          type: cartActions.ADD_TO_CART,
                          item: product,
                          quantity: item.quantity,
                          groupId:
                            item.machineInfo !== undefined
                              ? item.machineId
                                ? item.machineId
                                : UNIVERSAL_PRODUCTS_KEY
                              : UNIVERSAL_PRODUCTS_KEY,
                          groupDescription:
                            item.machineInfo !== undefined
                              ? item.machineName
                                ? item.machineName
                                : UNIVERSAL_PRODUCTS_KEY
                              : UNIVERSAL_PRODUCTS_KEY
                        });
                  })
              .catch((error) => {
                   console.warn('Error adding item to cart: ', error);
              });

      
      });
    }
  };
  const addItemsToCart = async (order: SFOrderHistory) => {
    const productIds = order.poDetails?.map((product) => product.productId || '') || [];
    // get the product data to show correct lead time on cart. (user story #19457)
    const response = await getProductsDetail({ productIds: productIds });
    const products: Product[] = response?.data?.items || [];

    if (sfOrders !== undefined) {
      order.poDetails?.forEach((item) => {
        // to get the leadtime from API,
        const itemDetails = products.find((product) => {
          const { id, price = 0, custPrice = 0, priceUnit } = product;
          const productPrice = +price?.toFixed(2);
          const productCustPrice = +custPrice?.toFixed(2);
          return (
            id === item.productId &&
            (productPrice === +(item?.unitPrice || 0) ||
              productCustPrice === +(item?.unitPrice || 0)) &&
            priceUnit === item.currency
          );
        });

        const product: Product = {
          id: item.productId as string,
          description: item.description as string,
          sku: item.partNumber as string,
          price: Number(item.unitPrice),
          priceUnit: item.currency,
          isPurchasable: true,
          leadTime: itemDetails?.leadTime || 0,
          stock: itemDetails?.stock || 1
        };
        AddProductToRemoteCart([ComposeCartItem(product, item.machineId as string, item.machineName as string)] as CartItem[])
              .unwrap()
              .then(() => {
                                // maintain temporay FE cartState to track cart count
                      dispatch({
                          type: cartActions.ADD_TO_CART,
                          item: product,
                          quantity: item.quantity,
                          groupId:
                            item.machineInfo !== undefined
                              ? item.machineId
                                ? item.machineId
                                : UNIVERSAL_PRODUCTS_KEY
                              : UNIVERSAL_PRODUCTS_KEY,
                          groupDescription:
                            item.machineInfo !== undefined
                              ? item.machineName
                                ? item.machineName
                                : UNIVERSAL_PRODUCTS_KEY
                              : UNIVERSAL_PRODUCTS_KEY
                        });
                  })
              .catch((error) => {
                   console.warn('Error adding item to cart: ', error);
              });
      });
    }
  };
  const downloadOrderDetails = () => {
    saveAs(linkToPdf as string, `order-quote-${poNumber}.pdf`);
  };
  const downloadQuoteDetails = (link: string) => {
    saveAs(link as string, `order-quote.pdf`);
  };
  const updateTableOnFilterChange = (val: string): void => {
    if (val === 'All') {
      setMachineId('all_machine');
    } else {
      const machine = machines?.find((m) => m.value === val);
      setMachineId(machine?.value as string);
    }
  };
  const handleTabClick = (tab: string) => {
    setCurrentTab(tab);
    searchParams.set('tab', tab);
    history.push({ search: searchParams.toString() });
  };
  const handleDeleteQuote = async (quote: PartQuote) => {
    try {
      await deleteRequestQuote({ requestId: quote.requestid }).unwrap();
      await refetchOrderQuotes();


      if (quote.status?.toLowerCase() === 'in progress') {
        // backend cart has been emptied as a result of the delete quote request.
        // The refetch is not needed to decide whether to clear the front-end cart.
        dispatch({ type: cartActions.EMPTY_CART });
      }else if(quote.status?.toLowerCase() === 'pending'){
        // Invalidate cart notifications for pending quotes. force a refetch of the cart notifications.
        dispatch(iopsApi.util.invalidateTags(['CartNotifications']));
      }

      ToastMsg({
        heading: 'Success',
        type: 'success',
        message: 'Quote with ID ' + quote.requestid + ' deleted successfully.'
      });
    } catch (error) {
      console.warn('Error deleting quote: ', error);
      ToastMsg({
        heading: 'Error',
        type: 'error',
        message: 'Unable to delete quote. Please try again.'
      });
    }
  };
  return (
    <>
      {/* <PageHeader heading={(t('orders') as string) + ' - ' + currentMachine} icon={OrdersIcon} /> */}
      <PageHeader heading={t('status') as string} icon={OrdersIcon} />
      <OrdersContainer>
        {showOrderDetails && (
          <OrderPdfModal
            showOrderDetails={showOrderDetails}
            hideOrderModal={hideOrderModal}
            downloadOrderDetails={downloadOrderDetails}
            t={t}
            fileUrl={linkToPdf}
          />
        )}
        {showQuoteDetails && (
          <QuotePdfModal
            showQuoteDetails={showQuoteDetails}
            hideQuoteModal={hideQuoteModal}
            downloadQuoteDetails={downloadQuoteDetails}
            t={t}
            quoteItem={quoteModalItem as PartQuote}
          />
        )}
        <div>
          <NavContainer>
            <TabNav
              items={[
                {
                  label: `${t(`orders`)}`,
                  isTabEnabled: true,
                  onClick: () => handleTabClick(ORDERS_TAB),
                  active: currentTab === ORDERS_TAB,
                  icon: <Cube />
                },
                {
                  label: `${t(`quotes`)}`,
                  isTabEnabled: true,
                  onClick: () => handleTabClick(QUOTES_TAB),
                  active: currentTab === QUOTES_TAB,
                  icon: <OrderQuote />
                }
              ]}
            />
          </NavContainer>
          {isTwoWaySyncUser && currentTab === ORDERS_TAB ? (
            <TwoWayOrderHistory
              sfOrders={sfOrders}
              addItemsToCart={addTwoWayItemsToCart}
              showOrderModal={showOrderModal}
              formatPODate={formatPODate}
              isDataFetching={isDataFetching}
              t={t}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalRecords={orders?.totalRecords as number}
              setOrderSearchKey={setOrderSearchKey}
              setOrderFilter={setOrderFilter}
            />
          ) : isTwoWaySyncUser && currentTab === QUOTES_TAB ? (
            <TwoWayOrderQuote
              orderQuotes={(orderQuotes?.requestQuoteDetails as PartQuote[]) || []}
              formatPODate={formatPODate}
              t={t}
              isDataFetching={isQuoteFetching}
              showQuoteModal={showQuoteModal}
              currentPage={currentQuotePage}
              setCurrentPage={setCurrentQuotePage}
              totalRecords={orderQuotes?.totalRecords as number}
              handleDeleteQuote={handleDeleteQuote}
               setQuoteSearchKey={setQuoteSearchKey}
               setQuoteFilter={setQuoteFilter}
            />
          ) : (
            <></>
          )}
          {!isTwoWaySyncUser && currentTab === ORDERS_TAB ? (
            <OrderHistory
              sfOrders={sfOrders}
              addItemsToCart={addItemsToCart}
              showOrderModal={showOrderModal}
              formatPODate={formatPODate}
              t={t}
              updateTableOnFilterChange={updateTableOnFilterChange}
              isDataFetching={isDataFetching}
              totalRecords={orders?.totalRecords as number}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          ) : !isTwoWaySyncUser && currentTab === QUOTES_TAB ? (
            <QuoteRequest
              orderQuotes={(orderQuotes?.requestQuoteDetails as PartQuote[]) || []}
              formatPODate={formatPODate}
              t={t}
              isDataFetching={isQuoteFetching}
              showQuoteModal={showQuoteModal}
              currentPage={currentQuotePage}
              setCurrentPage={setCurrentQuotePage}
              totalRecords={orderQuotes?.totalRecords as number}
            />
          ) : (
            <></>
          )}
        </div>
      </OrdersContainer>
    </>
  );
};

export default NewSFOrdersTable;
